import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography } from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  FileDownload as ExportIcon,
  InfoOutlined,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";

const CDCConfigurationReadOnly = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [filtersActive, setFiltersActive] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
    title: "",
  });

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type, title: "" });
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getCDCConfigurations();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table?.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Configurazione CDC");
      XLSX.writeFile(wb, "Configurazione_CDC_Data.xlsx");
    }
  };

  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }
      const columns = [
        {
          title: "CDC",
          field: "cdc",
          widthGrow: 1,
          headerFilter: "input",
        },
        {
          title: "Descrizione",
          field: "desc_CDC",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "Coord",
          field: "coord",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "DPT",
          field: "dpt",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "Responsabile",
          field: "responsabile",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "Local/Corporate",
          field: "local_Corporate",
          widthGrow: 2,
          headerFilter: "input",
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "cdc", dir: "asc" }],
        rowHeight: 40,
        selectable: false, // Disabilitata la selezione in modalità sola lettura
        responsiveLayout: false,
        autoResize: false,
        clipboard: true,
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Visualizzazione CDC", href: "/cdc-view" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Visualizzazione CDC
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi filtri" : "Mostra filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);

                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          mb: 2,
          bgcolor: "#E3F2FD", // Colore blu più chiaro per differenziare dalla pagina di gestione
          border: "1px solid #90CAF9",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#1976D2" }} />
        <Typography variant="body2">
          Visualizzazione di sola lettura dei CDC. Questa tabella mostra tutti i
          dati dei Centri di Costo.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Q8SnackBar
        title={snackbar.title}
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default CDCConfigurationReadOnly;
